/**
 * Privacy Policy Page
 * Privacy practices for Kata.fit
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicyPage = () => {
  const effectiveDate = 'February 1, 2026';
  const lastUpdated = 'February 1, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="back-link">← Back to App</Link>
          <h1>Privacy Policy</h1>
          <p className="meta">Effective Date: {effectiveDate} | Last Updated: {lastUpdated}</p>
        </header>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Kata.fit ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, share, and protect your personal information when you use 
            our fitness tracking and AI-powered application.
          </p>
          <p>
            By using Kata.fit, you agree to the collection and use of information in accordance 
            with this policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Account Information</h3>
          <ul>
            <li>Email address</li>
            <li>Display name</li>
            <li>Profile photo (optional)</li>
            <li>Date of birth (for age verification and personalization)</li>
            <li>Timezone</li>
          </ul>

          <h3>2.2 Health and Fitness Data</h3>
          <ul>
            <li>Workout logs (exercises, sets, reps, weights)</li>
            <li>Meal and nutrition logs</li>
            <li>Body measurements (weight, body fat, etc.)</li>
            <li>Progress photos and videos</li>
            <li>Health conditions and dietary restrictions (optional, for personalization)</li>
          </ul>

          <h3>2.3 Technical and Sensor Data</h3>
          <ul>
            <li>Device information and browser type</li>
            <li>IP address and location data (including precise location for gym detection feature)</li>
            <li>App usage and interaction data</li>
            <li>Crash reports and performance data</li>
            <li>Contact list information (only when you explicitly select contacts to invite)</li>
          </ul>

          <h3>2.4 AI Interaction Data</h3>
          <ul>
            <li>Queries and messages sent to AI features</li>
            <li>AI-generated recommendations and responses</li>
            <li>Exercise videos submitted for pose analysis</li>
          </ul>

          <h3>2.5 Geolocation Data</h3>
          <ul>
            <li>Location tracking is <strong>disabled by default</strong> and must be explicitly enabled in your user settings</li>
            <li>When enabled, precise location is used for automatic gym detection during workouts</li>
            <li>Location data is only collected while the app is open and you are actively using it</li>
            <li>Per-set location data may be stored to associate workouts with gyms you visit</li>
            <li>You can disable location tracking at any time in your profile settings, which immediately stops all location collection</li>
          </ul>

          <h3>2.6 Contact List Data</h3>
          <ul>
            <li>Selected contacts (if allowed) for sending friend invites</li>
            <li>We only process the specific contacts you choose to invite</li>
            <li>We do not store or sync your entire address book</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Kata.fit service</li>
            <li>Personalize your fitness and nutrition recommendations</li>
            <li>Generate AI-powered training insights and form feedback</li>
            <li>Enable social features (dojos, friends, messaging)</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send important notifications about your account</li>
            <li>Improve our algorithms and service quality</li>
            <li>Ensure security and prevent fraud</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Third-Party Services</h2>
          <p>We use the following third-party services that may process your data:</p>

          <h3>4.1 Authentication (Clerk)</h3>
          <p>
            We use Clerk for user authentication. Clerk processes your email and authentication 
            credentials. See their privacy policy at{' '}
            <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer">
              clerk.com/privacy
            </a>.
          </p>

          <h3>4.2 AI Services (OpenRouter)</h3>
          <p>
            AI features are powered by OpenRouter, which routes requests to various large language 
            model providers. When you use AI features:
          </p>
          <ul>
            <li>Your queries and relevant fitness data are sent to AI models</li>
            <li>Exercise videos may be analyzed by computer vision models</li>
            <li>OpenRouter and downstream AI providers may process this data</li>
          </ul>
          <p>
            We minimize personally identifiable information in AI requests. Review OpenRouter's 
            privacy policy at{' '}
            <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer">
              openrouter.ai/privacy
            </a>.
          </p>

          <h3>4.3 Payments (Paddle)</h3>
          <p>
            Subscription payments are processed by Paddle, who acts as the Merchant of Record. 
            Paddle processes payment information directly and shares only subscription status with us. 
            See Paddle's privacy policy at{' '}
            <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">
              paddle.com/legal/privacy
            </a>.
          </p>

          <h3>4.4 Email Communications (Resend)</h3>
          <p>
            We use Resend for transactional emails (notifications, verification). See their 
            privacy policy at{' '}
            <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
              resend.com/legal/privacy-policy
            </a>.
          </p>

          <h3>4.5 Hosting and Infrastructure (Heroku)</h3>
          <p>
            Our application is hosted on Heroku, a platform as a service (PaaS) provided by Salesforce. 
            Data is stored on secure infrastructure managed by Heroku (typically Amazon Web Services). 
            By using the Service, you acknowledge your data may be transferred to and processed in 
            facilities controlled by Heroku. See Salesforce/Heroku's privacy policy at{' '}
            <a href="https://www.salesforce.com/company/privacy/" target="_blank" rel="noopener noreferrer">
              salesforce.com/company/privacy
            </a>.
          </p>

          <h3>4.6 Database Services (MongoDB Atlas)</h3>
          <p>
            We use MongoDB Atlas to store and manage your data securely in the cloud. MongoDB Atlas 
            is a managed database service that adheres to high security standards including 
            encryption at rest and in transit. See MongoDB's privacy policy at{' '}
            <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
              mongodb.com/legal/privacy-policy
            </a>.
          </p>
        </section>

        <section>
          <h2>5. Data Sharing</h2>
          <p>We may share your information:</p>
          <ul>
            <li><strong>With your consent:</strong> When you share data with dojos, friends, or publicly</li>
            <li><strong>With service providers:</strong> Third parties that help us operate the Service</li>
            <li><strong>For legal reasons:</strong> When required by law or to protect rights and safety</li>
            <li><strong>Business transfers:</strong> In case of merger, acquisition, or sale of assets</li>
          </ul>
          <p>
            We do not sell your personal information to third parties for marketing purposes.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>We retain your data as follows:</p>
          <ul>
            <li><strong>Account data:</strong> As long as your account is active</li>
            <li><strong>Fitness data:</strong> As long as your account is active, unless you delete it</li>
            <li><strong>AI interaction logs:</strong> Up to 90 days for service improvement</li>
            <li><strong>After account deletion:</strong> Data is deleted within 30 days, except 
            where retention is required by law</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate data</li>
            <li><strong>Deletion:</strong> Request deletion of your data</li>
            <li><strong>Portability:</strong> Export your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
          </ul>
          <p>
            To exercise these rights, contact us at privacy@kata.fit or use the in-app 
            Import/Export feature for data export.
          </p>
        </section>

        <section>
          <h2>8. Security</h2>
          <p>We implement appropriate security measures including:</p>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of sensitive data at rest</li>
            <li>Access controls and authentication</li>
            <li>Regular security reviews and updates</li>
            <li>Secure coding practices</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure. We cannot 
            guarantee absolute security but strive to protect your data.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Kata.fit is not intended for children under 16. We do not knowingly collect 
            information from children under 16. If you believe we have collected such 
            information, please contact us immediately.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place for such transfers, including 
            standard contractual clauses where applicable.
          </p>
        </section>

        <section>
          <h2>11. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies for:
          </p>
          <ul>
            <li>Authentication and session management</li>
            <li>Remembering your preferences</li>
            <li>Analytics to improve the Service</li>
          </ul>
          <p>
            You can control cookies through your browser settings, but some features may 
            not work properly without them.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of 
            significant changes via email or in-app notification. Your continued use after 
            changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>For privacy-related questions or concerns:</p>
          <p>
            <strong>Email:</strong> privacy@kata.fit<br />
            <strong>General Support:</strong> team@kata.fit
          </p>
        </section>

        <footer className="legal-footer">
          <div className="footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms-and-conditions">Terms & Conditions</Link>
            <Link to="/refund">Refund Policy</Link>
            <Link to="/support">Support</Link>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Kata.fit. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
